import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";
import { colors, cardShadow } from "../constants";
import GlassCard from "./GlassCard";
import TeamIcon from "@/components/TeamIcon";

// Result Card - Refined Compact Layout
const ResultCard = ({ match, matchNumber }) => {
  const userPrediction = match.user_prediction;
  const totalRuns = match.total_runs;
  const [animatedTotal, setAnimatedTotal] = useState(0);
  const [animatedPrediction, setAnimatedPrediction] = useState(0);
  const [showReward, setShowReward] = useState(false);

  // Animate numbers on mount
  useEffect(() => {
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedTotal(Math.round(totalRuns * easeOut));
      if (userPrediction) {
        setAnimatedPrediction(Math.round(userPrediction * easeOut));
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Show reward after numbers finish
        setTimeout(() => setShowReward(true), 200);
      }
    };

    requestAnimationFrame(animate);
  }, [totalRuns, userPrediction]);

  const getPredictionResult = () => {
    if (!userPrediction) return null;
    const difference = userPrediction - totalRuns;
    const absDiff = Math.abs(difference);

    // Use actual credits from backend, not hardcoded values
    const credits = match.credits_earned ?? 0;
    let resultType = "";
    let resultLabel = "";

    // Use backend result_type when available (respects per-match close_threshold)
    const backendType = match.backend_result_type;
    if (backendType === "exact") {
      resultType = "perfect";
      resultLabel = "Perfect Prediction";
    } else if (backendType === "close") {
      resultType = "close";
      resultLabel = "Close Prediction";
    } else if (backendType === "overshoot") {
      resultType = "overshoot";
      resultLabel = "Overshoot";
    } else if (backendType === "undershoot") {
      resultType = "undershoot";
      resultLabel = "Undershoot";
    } else {
      // Fallback for matches without backend data — use per-match threshold
      const ct = match.reward_config?.close_threshold ?? match.close_threshold ?? 5;
      if (absDiff === 0) {
        resultType = "perfect";
        resultLabel = "Perfect Prediction";
      } else if (absDiff <= ct) {
        resultType = "close";
        resultLabel = "Close Prediction";
      } else {
        resultType = difference > 0 ? "overshoot" : "undershoot";
        resultLabel = difference > 0 ? "Overshoot" : "Undershoot";
      }
    }

    const colorMap = {
      perfect: "#01ff9a",
      close: "#01ff9a",
      overshoot: "#f97316",
      undershoot: "#3b82f6",
    };

    return {
      absDiff,
      credits,
      resultType,
      resultLabel,
      color: colorMap[resultType],
      isPerfect: resultType === "perfect" || resultType === "close",
      isCloseEnough: false,
    };
  };

  const result = getPredictionResult();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="result-card"
    >
      <GlassCard padding="p-5">
        {/* Match Series & Location Box */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
            data-testid="match-series-box"
          >
            <span className="text-sm font-bold text-white">
              M{matchNumber || match.match_number || "1"}
            </span>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-white/50" />
              <span className="text-xs text-white/50">
                {match.venue?.split(",").pop()?.trim() || "Mumbai"}
              </span>
            </div>
          </div>
        </div>

        {/* Teams Row - Compact */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TeamIcon team={match.team_a} size="sm" />
            <div>
              <p className="text-white font-semibold text-sm">{match.team_a}</p>
              <p className="text-lg font-bold text-white">
                {match.team_a_score}
                <span className="text-xs font-normal text-white/40">/{match.team_a_wickets}</span>
              </p>
            </div>
          </div>
          <span className="text-[10px] font-medium text-white/30">VS</span>
          <div className="flex items-center gap-2 text-right">
            <div>
              <p className="text-white font-semibold text-sm">{match.team_b}</p>
              <p className="text-lg font-bold text-white">
                {match.team_b_score}
                <span className="text-xs font-normal text-white/40">/{match.team_b_wickets}</span>
              </p>
            </div>
            <TeamIcon team={match.team_b} size="sm" />
          </div>
        </div>

        {/* Winner Text - Subtle */}
        {match.result_text && (
          <p className="text-[11px] text-center mb-5" style={{ color: "rgba(255,255,255,0.4)" }}>
            {match.result_text}
          </p>
        )}

        {/* Summary Row - Single Horizontal Line */}
        {userPrediction ? (
          <>
            {/* Divider */}
            <div className="w-full h-px mb-5" style={{ background: "rgba(255,255,255,0.08)" }} />

            {/* Stats Row */}
            <div className="flex items-center justify-between mb-5" data-testid="stats-row">
              {/* Final Total */}
              <div className="flex-1 text-center">
                <p
                  className="text-[10px] font-medium mb-1"
                  style={{ color: "rgba(255,255,255,0.5)" }}
                >
                  Final Total
                </p>
                <motion.p className="text-xl font-bold text-white" key={animatedTotal}>
                  {animatedTotal}
                </motion.p>
              </div>

              {/* Vertical Divider */}
              <div className="w-px h-10" style={{ background: "rgba(255,255,255,0.08)" }} />

              {/* Your Prediction */}
              <div className="flex-1 text-center">
                <p
                  className="text-[10px] font-medium mb-1"
                  style={{ color: "rgba(255,255,255,0.5)" }}
                >
                  Your Prediction
                </p>
                <motion.p className="text-xl font-bold text-white" key={animatedPrediction}>
                  {animatedPrediction}
                </motion.p>
              </div>

              {/* Vertical Divider */}
              <div className="w-px h-10" style={{ background: "rgba(255,255,255,0.08)" }} />

              {/* Difference */}
              <div className="flex-1 text-center">
                <p
                  className="text-[10px] font-medium mb-1"
                  style={{ color: "rgba(255,255,255,0.5)" }}
                >
                  Difference
                </p>
                <motion.p
                  className="text-xl font-bold"
                  style={{ color: result?.color }}
                  initial={{ scale: 1 }}
                  animate={{ scale: showReward ? [1, 1.1, 1] : 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {result?.absDiff}
                </motion.p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px mb-5" style={{ background: "rgba(255,255,255,0.08)" }} />

            {/* Result Highlight Block */}
            <AnimatePresence>
              {showReward && result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-[14px] p-4 text-center"
                  style={{
                    background:
                      result.isPerfect || result.isCloseEnough
                        ? `linear-gradient(135deg, ${result.color}12, ${result.color}06)`
                        : `${result.color}08`,
                    border: `1px solid ${result.color}30`,
                    boxShadow: result.isPerfect ? `0 0 20px ${result.color}15` : "none",
                  }}
                  data-testid="reward-block"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <motion.span
                      className="text-base font-bold"
                      style={{ color: result.color }}
                      animate={result.isPerfect ? { scale: [1, 1.02, 1] } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      {result.resultLabel}
                    </motion.span>
                    {result.isPerfect && <span>🎯</span>}
                    {result.isCloseEnough && <span>👏</span>}
                  </div>

                  {result.credits > 0 && (
                    <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                      You've won{" "}
                      <span
                        className="font-bold"
                        style={{
                          background: result.isCloseEnough
                            ? "linear-gradient(90deg, #facc15, #fef08a)"
                            : "linear-gradient(90deg, #01ff9a, #acffe5)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {result.credits} Hudle Credits
                      </span>
                    </p>
                  )}

                  {result.credits === 0 && !result.isPerfect && (
                    <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                      {result.resultLabel === "Overshoot"
                        ? "You over-predicted."
                        : "You under-predicted."}{" "}
                      Try again next game!
                    </p>
                  )}

                  {result.credits === 0 && result.isPerfect && (
                    <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                      {result.resultType === "perfect" ? "You nailed it!" : "So close!"} Credits
                      will be credited soon.
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          /* No Prediction State */
          <div className="text-center py-4">
            <div
              className="inline-block px-4 py-2.5 rounded-full text-xs font-medium"
              style={{
                background: "rgba(255,255,255,0.05)",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              You didn't predict this match
            </div>
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
};

export default ResultCard;
